import {
  useState,
  useMemo,
  useCallback,
  memo,
  useEffect,
  useLayoutEffect,
  Suspense,
  lazy,
} from 'react';
import { connect, useSelector } from 'react-redux';
import { bindActionCreators } from 'redux';
import Footer from './footer/footer';
import TableSkeleton from './table/tableSkeleton/TableSkeleton';
import { useGoToOrder, usePay, useIsCheckVisible } from './checkListHandler';
import {
  getSeat,
  clearCart,
  changeCartLoadingStatus,
  removePosition,
  pushCartItems,
  setPosition,
  setCurrentPage as setOrdersCurrentPage,
  setOrderNavLoading,
  setSeat,
} from '@reducers/orders';
import { sortTable } from '@utility/helperFunction';
import { SORT_ASC, SORT_DESC } from './constants';
import { usePrintModal } from '@components/checkListV2/tableRow/customHooks/usePrintModal';
import Loader from '@components/common/Loader/newLoaderModal';
import ensureCardReaderConnection from '@utility/ensureCardReaderConnection';
import { setErrorPopUp } from '@reducers/notification';
import { LABEL } from '@veriables';

const Table = lazy(() => import('./table'));

const CheckList = ({
  tableData,
  pageNo,
  serverAppTheme,
  activeRow,
  setActiveRow,
  auth,
  userInfo,
  allChecksVisible,
  isMyCheck,
  employeeJobHashTable,
  loading,
  getSeat,
  clearCart,
  changeCartLoadingStatus,
  removePosition,
  pushCartItems,
  setPosition,
  setOrdersCurrentPage,
  setOrderNavLoading,
  setSeat,
  isOnlyOrderMode,
  setErrorPopUp,
}) => {
  const [pagination] = useState(6);
  const [currentPage, setCurrentPage] = useState(pageNo + 1);
  const [sortKey, setSortKey] = useState('stationNumberForSort');
  const [sortBy, setSortBy] = useState(SORT_ASC);
  const deviceInfo = useSelector(state => state.oauth.deviceInformation);
  const [cardReaderConnectionState, setCardReaderConnectionState] = useState({
    isLoading: false,
    message: '',
  });

  const suppressZeroDollarChecks = useMemo(
    () => auth?.serverAppFeatures?.suppressZeroDollarChecks ?? false,
    [auth]
  );

  const teamId = useMemo(
    () => auth?.jobs?.find(j => j.id === userInfo?.jobId)?.jobTeamId,
    [auth?.jobs, userInfo?.jobId]
  );

  const jobList = useMemo(
    () => auth?.jobs?.filter(j => j.jobTeamId === teamId) ?? [],
    [auth?.jobs, teamId]
  );

  const sortColumn = useCallback(
    columnName => {
      const nextSortBy = columnName === sortKey && sortBy === SORT_ASC ? SORT_DESC : SORT_ASC;
      setSortKey(columnName);
      setSortBy(nextSortBy);
    },
    [sortKey, sortBy]
  );

  const isCheckVisible = useIsCheckVisible({
    isMyCheck,
    userInfo,
    auth,
    employeeJobHashTable,
    jobList,
    teamId,
    allChecksVisible,
  });

  const tableArray = useMemo(() => {
    if (!tableData) return [];
    return Object.values(tableData).filter(check => {
      if (allChecksVisible) {
        if (!check.open) return false;
        if (suppressZeroDollarChecks && check.totalAmount === 0) return false;
      }

      return isCheckVisible(check);
    });
  }, [tableData, allChecksVisible, suppressZeroDollarChecks, isCheckVisible]);

  const sortedArray = useMemo(() => {
    if (!tableArray.length) return tableArray;
    const dir = sortBy === SORT_ASC ? 1 : -1;
    if (typeof tableArray[0]?.[sortKey] === 'number') {
      return [...tableArray].sort((a, b) => ((a[sortKey] ?? 0) - (b[sortKey] ?? 0)) * dir);
    }
    return sortTable(sortKey, [...tableArray], sortBy);
  }, [tableArray, sortKey, sortBy]);

  const totalPages = Math.ceil(sortedArray.length / pagination);

  const paginatedData = useMemo(
    () =>
      sortedArray.length > pagination
        ? sortedArray.slice(
          (currentPage - 1) * pagination,
          currentPage * pagination
        )
        : sortedArray,
    [sortedArray, currentPage, pagination]
  );

  const updateActiveToFirstCheck = useCallback(() => {
    if (paginatedData?.length > 0 && paginatedData?.[0]?.id) {
      setActiveRow(paginatedData[0].id);
      setSeat(paginatedData[0]);
    } else {
      setActiveRow(null);
      setSeat(null);
    }
  }, [paginatedData, setActiveRow, setSeat]);

  useLayoutEffect(() => {
    // when allChecks/openChecks is selected from hamburger.

    // resets active item to the first check.
    updateActiveToFirstCheck();
    // resets currentPage to 1;
    setCurrentPage(pageNo + 1);
  }, [allChecksVisible]);

  useLayoutEffect(() => {
    // resets active item to the first check, when navigating through the pages.
    updateActiveToFirstCheck();
  }, [currentPage]);

  useLayoutEffect(() => {
    // resets active item and page when sort order changes.
    setCurrentPage(1);
    updateActiveToFirstCheck();
  }, [sortKey, sortBy]);

  useLayoutEffect(() => {
    if (paginatedData?.length === 0 && activeRow != null) {
      setActiveRow(null);
      setSeat(null);
    } else if (paginatedData?.length > 0 && activeRow != null) {
      const isPresent = paginatedData.some(row => row.id === activeRow);
      if (!isPresent) {
        setActiveRow(paginatedData[0].id);
        setSeat(paginatedData[0]);
      }
    }
  }, [paginatedData, activeRow, setActiveRow, setSeat]);

  const pay = usePay();

  const goToOrder = useGoToOrder({
    getSeat,
    clearCart,
    changeCartLoadingStatus,
    removePosition,
    pushCartItems,
    setPosition,
    setCurrentPage: setOrdersCurrentPage,
    setOrderNavLoading,
  });

  const handlePayClick = useCallback(
    async (data, currentPage) => {
      if (cardReaderConnectionState.isLoading) {
        return;
      }

      setCardReaderConnectionState({
        isLoading: true,
        message: LABEL().CARD_READER_CHECKING_CONNECTION,
      });

      try {
        const cardReaderResult = await ensureCardReaderConnection({
          maxReconnectAttempts: 2,
          skipConnectionCheck: Boolean(deviceInfo?.deviceGroup?.serverAppSettings?.disablePayment) || deviceInfo?.deviceGroup?.paymentTypes?.creditCard === false,
          onProgress: message => {
            console.log('ensureCardReaderConnection - onProgress:', message);
            setCardReaderConnectionState({
              isLoading: true,
              message,
            });
          },
        });

        if (cardReaderResult.ok) {
          console.log(
            'ensureCardReaderConnection - succeeded:',
            cardReaderResult.message || cardReaderResult
          );
          pay(data, currentPage);
          return;
        }

        console.error(
          'ensureCardReaderConnection - failed:',
          cardReaderResult.errorMessage,
          cardReaderResult.error
        );

        setErrorPopUp({
          res: LABEL().CARD_READER_PAY_PREVENTION_ERROR,
          fullWidth: true,
        });
      } finally {
        setCardReaderConnectionState({
          isLoading: false,
          message: '',
        });
      }
    },
    [cardReaderConnectionState.isLoading, setErrorPopUp]
  );

  const { onOpenPrint, jsx: printModalJSX } = usePrintModal({ serverAppTheme });

  return (
    <div className="h-100 w-75 pr-2">
      {cardReaderConnectionState.isLoading && (
        <Loader message={cardReaderConnectionState.message} />
      )}
      <div className="h-90">
        <Suspense fallback={<TableSkeleton />}>
          {loading && paginatedData?.length === 0 ? (
            <TableSkeleton />
          ) : (
            <Table
              paginatedData={paginatedData}
              serverAppTheme={serverAppTheme}
              onOrderClick={goToOrder}
              onPayClick={handlePayClick}
              activeRow={activeRow}
              setActiveRow={setActiveRow}
              setSeat={setSeat}
              sortColumn={sortColumn}
              activeSortKey={sortKey}
              sortBy={sortBy}
              onOpenPrint={onOpenPrint}
              thirdPartyAppName={auth?.thirdPartyAppName}
              isOnlyOrderMode={isOnlyOrderMode}
            />
          )}
        </Suspense>
      </div>
      <div className="h-10">
        <Footer
          totalPages={totalPages}
          currentPage={currentPage}
          setCurrentPage={setCurrentPage}
          serverAppTheme={serverAppTheme}
        />
      </div>
      {printModalJSX}
    </div>
  );
};

const mapStateToProps = ({ storeChecks, optionsMenus, oauth }) => ({
  auth: oauth.authInfo,
  userInfo: oauth.loggedInUserData,
  allChecksVisible: optionsMenus.allChecksVisible,
  isMyCheck: storeChecks.isOn,
  employeeJobHashTable: oauth.employeeJobHashTable,
  loading: storeChecks.loading,
  isOnlyOrderMode: oauth.deviceInformation?.deviceGroup?.serverAppSettings?.disableOrdering
});

const mapDispatchToProps = dispatch =>
  bindActionCreators(
    {
      getSeat,
      clearCart,
      changeCartLoadingStatus,
      removePosition,
      pushCartItems,
      setPosition,
      setOrdersCurrentPage,
      setOrderNavLoading,
      setSeat,
      setErrorPopUp,
    },
    dispatch
  );

const MemoizedCheckList = memo(CheckList);

export default connect(mapStateToProps, mapDispatchToProps)(MemoizedCheckList);
